import { formatFullStreet } from "../../util/format";
import {
  mapCountry,
  mapDateDocumentToPageModel,
  mapDatePageModelToRequest,
  mapNationality,
  mapNationalityString,
  mapPhoneDocumentToPageModel,
  mapPhonePageModelToRequest,
} from "../shared/mappers";
import { defaults } from "./defaults";
import {
  Attorney,
  AttorneyValues,
  PersonType,
  PowerOfAttorneyModel,
} from "./types";
import {
  BasicAccountApplicationDocument,
  type CorrespondenceLanguage,
  NaturalPersonDocument,
  PowerOfAttorneyDto,
} from "../../api/commercial/generated-types";
import { LanguageFrontendType } from "../shared/components/language/types";

const getAttorneyType = (
  document: BasicAccountApplicationDocument,
): Attorney => {
  if (document.noPowerOfAttorney) {
    return AttorneyValues.NONE as Attorney;
  }

  if (document.powerOfAttorneys.length === 0 && !document.noPowerOfAttorney) {
    return AttorneyValues.NOT_CHOSEN as Attorney;
  }

  if (document.adminIsPowerOfAttorney) {
    return AttorneyValues.ADMIN as Attorney;
  }
  if (document.powerOfAttorneys.length !== 0) {
    return AttorneyValues.OTHER as Attorney;
  }
};

const mapPersonDocumentToPageModel = (
  personDocument: NaturalPersonDocument,
  isSameAsAdmin: boolean,
): PersonType => {
  return {
    nationality: mapNationality(personDocument.nationality),
    birthdate: mapDateDocumentToPageModel(personDocument.birthdate),
    gender: personDocument.gender,
    firstName: personDocument.firstName,
    lastName: personDocument.lastName,
    fullStreet: formatFullStreet(personDocument.address),
    address: {
      ...personDocument.address,
      country: mapCountry(
        personDocument.address ? personDocument.address.country : null,
      ),
    },
    mobile: mapPhoneDocumentToPageModel(personDocument.mobile),
    emailBusiness: personDocument.emailBusiness,
    isSameAsAdmin,
  };
};

const mapDocumentToPageModel = (
  document: BasicAccountApplicationDocument,
): PowerOfAttorneyModel => {
  const admin = document.admin;

  const defaultValues = {
    ...defaults,
    adminAttorneysData: [
      {
        ...defaults.adminAttorneysData[0],
        ...mapPersonDocumentToPageModel(admin, true),
      },
    ],
  };

  let documentAttorneysData: PersonType[] = [];
  if (document.adminIsPowerOfAttorney) {
    documentAttorneysData = [mapPersonDocumentToPageModel(admin, true)];
  }

  documentAttorneysData = documentAttorneysData.concat(
    document.powerOfAttorneys.map(
      (att: NaturalPersonDocument): PersonType =>
        mapPersonDocumentToPageModel(att, false),
    ),
  );

  const attorneyType = getAttorneyType(document);

  return {
    attorneyType:
      attorneyType === AttorneyValues.NOT_CHOSEN
        ? (AttorneyValues.ADMIN as Attorney)
        : attorneyType,
    attorneysData:
      !document.adminIsPowerOfAttorney && documentAttorneysData.length !== 0
        ? documentAttorneysData
        : defaultValues.attorneysData,
    adminAttorneysData:
      attorneyType === AttorneyValues.NOT_CHOSEN
        ? defaultValues.adminAttorneysData
        : attorneyType === AttorneyValues.ADMIN
          ? documentAttorneysData
          : defaultValues.adminAttorneysData,
  };
};

const mapPageModelToRequest = (
  data: PowerOfAttorneyModel,
  basicAccountApplicationRef: string,
): PowerOfAttorneyDto => {
  return {
    basicAccountApplicationRef,
    attorneys:
      data.attorneyType === AttorneyValues.ADMIN ||
      data.attorneyType === AttorneyValues.OTHER
        ? (data.attorneyType === AttorneyValues.ADMIN
            ? data.adminAttorneysData
            : data.attorneysData
          ).map((a) => {
            return {
              gender: a.gender,
              firstName: a.firstName,
              lastName: a.lastName,
              address: { ...a.address, country: mapCountry(a.address.country) },
              mobile: mapPhonePageModelToRequest(a.mobile),
              emailBusiness: a.emailBusiness,
              birthdate: mapDatePageModelToRequest(a.birthdate),
              nationality: mapNationalityString(a.nationality),
              isSameAsAdmin: a.isSameAsAdmin || false,
            };
          })
        : [],
  };
};

const mapFrontendLanguageToCorrespondenceLanguage = (
  language: LanguageFrontendType,
): CorrespondenceLanguage => {
  switch (language) {
    case LanguageFrontendType.de:
      return "GERMAN";
    case LanguageFrontendType.en:
      return "ENGLISH";
    case LanguageFrontendType.fr:
      return "FRENCH";
    case LanguageFrontendType.it:
      return "ITALIAN";
    default:
      return "GERMAN";
  }
};

const PowerOfAttorneyMapper = {
  mapPageModelToRequest,
  mapDocumentToPageModel,
  mapFrontendLanguageToCorrespondenceLanguage,
};

export default PowerOfAttorneyMapper;
